import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileArrowDown, Plus, PencilSimple, Trash, MagnifyingGlass, ArrowUp, ArrowDown, ArrowsDownUp, Export } from "@phosphor-icons/react";
import { useTableFeatures, exportCSV } from "@/hooks/useTableFeatures";
import { useBulkSelect } from "@/hooks/useBulkSelect";
import BulkActionBar from "@/components/BulkActionBar";
import { Checkbox } from "@/components/ui/checkbox";

const defaultForm = {
  client_id: "", client_name: "", application_number: "", ben: "", funding_year: "",
  frn: "", service_provider: "", spin: "", category_of_service: "category_1",
  total_funding_request: 0, discount_rate: 0, pia_status: "not_started",
  pia_review_date: "", pia_notes: "", fcdl_date: "", status: "draft", notes: "",
};

const piaStatusColors = {
  not_started: "bg-gray-100 text-[#6B7280] border-gray-200",
  in_review: "bg-[#002FA7]/10 text-[#002FA7] border-[#002FA7]/20",
  info_requested: "bg-[#FFD100]/10 text-[#946B00] border-[#FFD100]/20",
  approved: "bg-[#00B341]/10 text-[#00B341] border-[#00B341]/20",
  denied: "bg-[#FF2A00]/10 text-[#FF2A00] border-[#FF2A00]/20",
};
const piaLabels = {
  not_started: "Not Started", in_review: "In Review", info_requested: "Info Requested",
  approved: "Approved", denied: "Denied",
};
const statusColors = {
  draft: "bg-gray-100 text-[#6B7280] border-gray-200",
  submitted: "bg-[#002FA7]/10 text-[#002FA7] border-[#002FA7]/20",
  approved: "bg-[#00B341]/10 text-[#00B341] border-[#00B341]/20",
  committed: "bg-[#00B341]/10 text-[#00B341] border-[#00B341]/20",
  denied: "bg-[#FF2A00]/10 text-[#FF2A00] border-[#FF2A00]/20",
};

const fmt = (n) => {
  if (!n || n === 0) return "$0";
  return `$${Number(n).toLocaleString()}`;
};

export default function Form471() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...defaultForm });
  const bulk = useBulkSelect();
  const bulkComplete = () => { bulk.clear(); load(); };

  const { search, setSearch, sortKey, sortDir, toggleSort, filters, setFilter, clearFilters, processed, activeFilterCount } = useTableFeatures(items);
  const fundingYears = [...new Set(items.map((i) => i.funding_year).filter(Boolean))].sort().reverse();
  const csvCols = [
    { label: "App #", key: "application_number" }, { label: "Client", key: "client_name" }, { label: "BEN", key: "ben" },
    { label: "FY", key: "funding_year" }, { label: "FRN", key: "frn" }, { label: "Provider", key: "service_provider" },
    { label: "Amount", key: "total_funding_request" }, { label: "Discount", key: "discount_rate" },
    { label: "Bids", key: "bid_count" },
    { label: "PIA Status", key: "pia_status" }, { label: "Status", key: "status" },
  ];
  const SH = ({ k, children }) => (
    <TableHead className="py-3 px-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold cursor-pointer select-none hover:text-[#1A1D20]" onClick={() => toggleSort(k)}>
      <div className="flex items-center gap-1">{children}{sortKey === k ? (sortDir === "asc" ? <ArrowUp size={10} /> : <ArrowDown size={10} />) : <ArrowsDownUp size={10} className="opacity-30" />}</div>
    </TableHead>
  );

  useEffect(() => { load(); loadClients(); }, []);
  const load = async () => { try { const { data } = await api.get("/forms/471"); setItems(data); } catch { toast.error("Failed to load"); } };
  const loadClients = async () => { try { const { data } = await api.get("/clients"); setClients(data); } catch {} };

  const save = async () => {
    try {
      if (editing) { await api.put(`/forms/471/${editing}`, form); toast.success("Updated"); }
      else { await api.post("/forms/471", form); toast.success("Created"); }
      setOpen(false); load();
    } catch { toast.error("Failed to save"); }
  };

  const remove = async (id) => { if (!window.confirm("Delete?")) return; try { await api.delete(`/forms/471/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Failed"); } };
  const edit = (item) => { setForm({ ...item }); setEditing(item.id); setOpen(true); };
  const openNew = () => { setForm({ ...defaultForm }); setEditing(null); setOpen(true); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const selectClient = (id) => { const c = clients.find((cl) => cl.id === id); setForm((f) => ({ ...f, client_id: id, client_name: c ? c.name : "", ben: c ? c.ben : "" })); };

  return (
    <div className="p-6" data-testid="form471-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#6B7280] mb-1">E-Rate Forms</p>
          <h1 className="text-2xl sm:text-3xl tracking-tight font-semibold text-[#1A1D20]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>FCC Form 471 / PIA Status</h1>
          <p className="text-sm text-[#6B7280] mt-1">Description of Services Ordered and Certification</p>
        </div>
        <button data-testid="add-form471-btn" onClick={openNew} className="bg-[#002FA7] text-white hover:bg-[#002480] rounded-none font-medium px-4 py-2 text-sm flex items-center gap-2 transition-colors">
          <Plus size={16} weight="bold" /> Add Form 471
        </button>
      </div>

      <div className="mb-4 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
          <Input data-testid="search-form471" placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} className="rounded-none pl-9" />
        </div>
        <Select value={filters.status || "all"} onValueChange={(v) => setFilter("status", v)}>
          <SelectTrigger className="rounded-none w-[120px]"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent className="rounded-none"><SelectItem value="all">All</SelectItem><SelectItem value="draft">Draft</SelectItem><SelectItem value="submitted">Submitted</SelectItem><SelectItem value="approved">Approved</SelectItem><SelectItem value="committed">Committed</SelectItem><SelectItem value="denied">Denied</SelectItem></SelectContent>
        </Select>
        <Select value={filters.pia_status || "all"} onValueChange={(v) => setFilter("pia_status", v)}>
          <SelectTrigger className="rounded-none w-[150px]"><SelectValue placeholder="PIA Status" /></SelectTrigger>
          <SelectContent className="rounded-none"><SelectItem value="all">All PIA</SelectItem><SelectItem value="not_started">Not Started</SelectItem><SelectItem value="in_review">In Review</SelectItem><SelectItem value="info_requested">Info Requested</SelectItem><SelectItem value="approved">Approved</SelectItem><SelectItem value="denied">Denied</SelectItem></SelectContent>
        </Select>
        <Select value={filters.category_of_service || "all"} onValueChange={(v) => setFilter("category_of_service", v)}>
          <SelectTrigger className="rounded-none w-[120px]"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent className="rounded-none"><SelectItem value="all">All</SelectItem><SelectItem value="category_1">Cat 1</SelectItem><SelectItem value="category_2">Cat 2</SelectItem></SelectContent>
        </Select>
        <Select value={filters.funding_year || "all"} onValueChange={(v) => setFilter("funding_year", v)}>
          <SelectTrigger data-testid="filter-471-year" className="rounded-none w-[140px]"><SelectValue placeholder="All Years" /></SelectTrigger>
          <SelectContent className="rounded-none">
            <SelectItem value="all">All Years</SelectItem>
            {fundingYears.map((y) => <SelectItem key={y} value={y}>FY {y}</SelectItem>)}
          </SelectContent>
        </Select>
        {activeFilterCount > 0 && <button onClick={clearFilters} className="text-xs text-[#002FA7] hover:underline">Clear</button>}
        <button data-testid="export-form471-btn" onClick={() => exportCSV(processed, csvCols, "form471.csv")} className="ml-auto flex items-center gap-1.5 bg-transparent text-[#1A1D20] border border-[#1A1D20] hover:bg-[#F8F9FA] rounded-none font-medium px-3 py-2 text-xs transition-colors">
          <Export size={14} /> Export CSV
        </button>
      </div>

      <div className="border border-gray-200 bg-white overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="data-table-header">
              <TableHead className="py-3 px-3 w-[40px]"><Checkbox data-testid="select-all-form471" checked={bulk.selected.size === processed.length && processed.length > 0} onCheckedChange={() => bulk.toggleAll(processed.map(i => i.id))} /></TableHead>
              <SH k="application_number">App #</SH>
              <SH k="client_name">Client</SH>
              <SH k="funding_year">FY</SH>
              <SH k="frn">FRN</SH>
              <SH k="service_provider">Provider</SH>
              <SH k="total_funding_request">Amount</SH>
              <SH k="discount_rate">Discount</SH>
              <SH k="bid_count">Bids</SH>
              <SH k="pia_status">PIA Status</SH>
              <SH k="status">Status</SH>
              <TableHead className="py-3 px-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold w-[80px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {processed.length === 0 ? (
              <TableRow><TableCell colSpan={11} className="text-center py-8 text-[#6B7280] text-sm">No Form 471 records</TableCell></TableRow>
            ) : processed.map((item) => (
              <TableRow key={item.id} className="border-b border-gray-100 hover:bg-gray-50">
                <TableCell className="py-3 px-3"><Checkbox data-testid={`select-form471-${item.id}`} checked={bulk.selected.has(item.id)} onCheckedChange={() => bulk.toggle(item.id)} /></TableCell>
                <TableCell className="py-3 px-4 font-mono text-xs">{item.application_number || "—"}</TableCell>
                <TableCell className="py-3 px-4 text-[#1A1D20] font-medium">{item.client_name || "—"}</TableCell>
                <TableCell className="py-3 px-4 text-sm">{item.funding_year || "—"}</TableCell>
                <TableCell className="py-3 px-4 font-mono text-xs text-[#002FA7]">{item.frn || "—"}</TableCell>
                <TableCell className="py-3 px-4 text-sm">{item.service_provider || "—"}</TableCell>
                <TableCell className="py-3 px-4 text-sm font-medium">{fmt(item.total_funding_request)}</TableCell>
                <TableCell className="py-3 px-4 text-sm">{item.discount_rate > 0 ? `${item.discount_rate}%` : "—"}</TableCell>
                <TableCell className="py-3 px-4 text-sm font-medium text-[#1A1D20]" data-testid={`form471-bids-${item.id}`}>{item.bid_count > 0 ? item.bid_count : "—"}</TableCell>
                <TableCell className="py-3 px-4">
                  <Badge className={`rounded-none text-xs border ${piaStatusColors[item.pia_status] || piaStatusColors.not_started}`}>
                    {piaLabels[item.pia_status] || item.pia_status}
                  </Badge>
                </TableCell>
                <TableCell className="py-3 px-4"><Badge className={`rounded-none text-xs border capitalize ${statusColors[item.status] || statusColors.draft}`}>{item.status}</Badge></TableCell>
                <TableCell className="py-3 px-4">
                  <div className="flex items-center gap-1">
                    <button data-testid={`edit-form471-${item.id}`} onClick={() => edit(item)} className="p-1.5 hover:bg-gray-100"><PencilSimple size={14} className="text-[#6B7280]" /></button>
                    <button data-testid={`delete-form471-${item.id}`} onClick={() => remove(item.id)} className="p-1.5 hover:bg-red-50"><Trash size={14} className="text-[#FF2A00]" /></button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <BulkActionBar count={bulk.count} collection="form471" selectedIds={bulk.selected} onComplete={bulkComplete}
        statuses={[{value:"draft",label:"Draft"},{value:"submitted",label:"Submitted"},{value:"approved",label:"Approved"},{value:"committed",label:"Committed"},{value:"denied",label:"Denied"}]} />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none sm:max-w-[720px] max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{editing ? "Edit" : "Add"} Form 471</DialogTitle></DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Client</label>
                <Select value={form.client_id} onValueChange={selectClient}>
                  <SelectTrigger data-testid="form471-client-select" className="rounded-none"><SelectValue placeholder="Select client" /></SelectTrigger>
                  <SelectContent className="rounded-none max-h-[200px]">{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Application Number</label>
                <Input data-testid="form471-appnum-input" value={form.application_number} onChange={(e) => set("application_number", e.target.value)} className="rounded-none" />
              </div>
            </div>
            <div className="grid grid-cols-4 gap-4">
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">BEN</label>
                <Input data-testid="form471-ben-input" value={form.ben} onChange={(e) => set("ben", e.target.value)} className="rounded-none" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">FY</label>
                <Input data-testid="form471-fy-input" value={form.funding_year} onChange={(e) => set("funding_year", e.target.value)} className="rounded-none" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">FRN</label>
                <Input data-testid="form471-frn-input" value={form.frn} onChange={(e) => set("frn", e.target.value)} className="rounded-none" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Category</label>
                <Select value={form.category_of_service} onValueChange={(v) => set("category_of_service", v)}>
                  <SelectTrigger data-testid="form471-category-select" className="rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none"><SelectItem value="category_1">Cat 1</SelectItem><SelectItem value="category_2">Cat 2</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Service Provider</label>
                <Input data-testid="form471-provider-input" value={form.service_provider} onChange={(e) => set("service_provider", e.target.value)} className="rounded-none" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">SPIN</label>
                <Input data-testid="form471-spin-input" value={form.spin} onChange={(e) => set("spin", e.target.value)} className="rounded-none" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Total Funding Request ($)</label>
                <Input data-testid="form471-amount-input" type="number" value={form.total_funding_request} onChange={(e) => set("total_funding_request", parseFloat(e.target.value) || 0)} className="rounded-none" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Discount Rate (%)</label>
                <Input data-testid="form471-discount-input" type="number" value={form.discount_rate} onChange={(e) => set("discount_rate", parseFloat(e.target.value) || 0)} className="rounded-none" />
              </div>
            </div>

            {/* PIA Section */}
            <div className="border border-[#002FA7]/20 p-4 bg-[#002FA7]/5 mt-2">
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#002FA7] mb-3">PIA Inquiry Status</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">PIA Status</label>
                  <Select value={form.pia_status} onValueChange={(v) => set("pia_status", v)}>
                    <SelectTrigger data-testid="form471-pia-select" className="rounded-none bg-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-none">
                      <SelectItem value="not_started">Not Started</SelectItem>
                      <SelectItem value="in_review">In Review</SelectItem>
                      <SelectItem value="info_requested">Info Requested</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="denied">Denied</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Review Date</label>
                  <Input data-testid="form471-pia-date-input" type="date" value={form.pia_review_date} onChange={(e) => set("pia_review_date", e.target.value)} className="rounded-none bg-white" />
                </div>
              </div>
              <div className="mt-3">
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">PIA Notes</label>
                <textarea data-testid="form471-pia-notes-input" value={form.pia_notes} onChange={(e) => set("pia_notes", e.target.value)} className="w-full border border-gray-300 rounded-none px-3 py-2 text-sm focus:outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] min-h-[50px] bg-white" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">FCDL Date</label>
                <Input data-testid="form471-fcdl-input" type="date" value={form.fcdl_date} onChange={(e) => set("fcdl_date", e.target.value)} className="rounded-none" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Status</label>
                <Select value={form.status} onValueChange={(v) => set("status", v)}>
                  <SelectTrigger data-testid="form471-status-select" className="rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">
                    <SelectItem value="draft">Draft</SelectItem><SelectItem value="submitted">Submitted</SelectItem><SelectItem value="approved">Approved</SelectItem><SelectItem value="committed">Committed</SelectItem><SelectItem value="denied">Denied</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Notes</label>
              <textarea data-testid="form471-notes-input" value={form.notes} onChange={(e) => set("notes", e.target.value)} className="w-full border border-gray-300 rounded-none px-3 py-2 text-sm focus:outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] min-h-[60px]" />
            </div>
          </div>
          <DialogFooter>
            <button data-testid="cancel-form471-btn" onClick={() => setOpen(false)} className="bg-transparent text-[#1A1D20] border border-[#1A1D20] hover:bg-[#F8F9FA] rounded-none font-medium px-4 py-2 text-sm">Cancel</button>
            <button data-testid="save-form471-btn" onClick={save} className="bg-[#002FA7] text-white hover:bg-[#002480] rounded-none font-medium px-4 py-2 text-sm">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
